import { captureException } from '@sentry/nextjs';
import { isAfter, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import Decimal from 'decimal.js';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { remark } from 'remark';
import strip from 'strip-markdown';

import client from '@/apollo-client';
import Layout from '@/components/Layout';
import EventDetail, { EventDetailProps } from '@/components/Pages/EventDetail';
import TicketType from '@/components/Pages/TicketSelection/TicketType';
import {
  EventDocument,
  EventQuery,
  EventQueryVariables,
} from '@/generated/graphql';
import { getStatus } from '@/utils';

const now = new Date();

export default function EventPage(props: {
  event: NonNullable<EventQuery['event']>;
  description: string;
}) {
  const { event, description } = props;

  const itinerary = formatInTimeZone(
    event.startDateTime,
    event.venue.timezone,
    `EEE MMM dd, hh:mmaa zzz`, // Sun Apr 10, 10:00PM EST
    { locale: enUS },
  );

  const ticketTypes: TicketType[] = event.ticketTypes.map((ticketType) => {
    const base = new Decimal(ticketType.price.base.amount);
    const fee = new Decimal(ticketType.price.fee.amount);
    const taxRate = new Decimal(event.venue.taxRate.value).div(100);
    const total = base.add(fee);
    const tax = total.mul(taxRate);

    return {
      id: ticketType.id,
      name: ticketType.name,
      basePrice: base,
      fee: fee,
      tax: tax,
      maxPerCheckoutCount: ticketType.maxPerCheckoutCount,
      ticketLeftCount: ticketType.ticketLeftCount,
      currency: ticketType.price.base.currency ?? `USD`,
      saleStartsAt: parseISO(ticketType.saleStartsAt),
      saleEndsAt: parseISO(ticketType.saleEndsAt),
      revealedByPromoCode: ticketType.revealedByPromoCode,
      helperText: ticketType.helperText ?? null,
    };
  });

  const eventDetailProps: EventDetailProps = {
    id: event.id,
    imageUrl: event.imgUrl,
    venueImageUrl: event.venue.imgUrl,
    audienceMinimumAge: event.audienceMinimumAge,
    mapUrl: event.venue.mapUrl,
    title: event.title,
    venueId: event.venue.id,
    venueName: event.venue.name,
    venueAddress: event.venue.address,
    venueWebsiteUrl: event.venue.websiteUrl ?? null,
    itinerary: itinerary,
    status: getStatus({
      ticketTypes,
      isCancelled: event.isCancelled,
      isEnded: isAfter(now, parseISO(event.endDateTime)),
    }),
    description: event.description,
    artists: event.lineup.entries.map((entry) => {
      return {
        id: entry.performer.id,
        imageUrl: entry.performer.imgUrl,
        artistName: entry.performer.name,
      };
    }),
    ticketTypes: event.ticketTypes.map((ticketType) => {
      const base = new Decimal(ticketType.price.base.amount);
      const fee = new Decimal(ticketType.price.fee.amount);
      const taxRate = new Decimal(event.venue.taxRate.value).div(100);
      const total = base.add(fee);
      const tax = total.mul(taxRate);

      return {
        id: ticketType.id,
        name: ticketType.name,
        basePrice: base,
        fee: fee,
        tax: tax,
        maxPerCheckoutCount: ticketType.maxPerCheckoutCount,
        ticketLeftCount: ticketType.ticketLeftCount,
        currency: ticketType.price.base.currency ?? `USD`,
        saleStartsAt: parseISO(ticketType.saleStartsAt),
        saleEndsAt: parseISO(ticketType.saleEndsAt),
        revealedByPromoCode: ticketType.revealedByPromoCode,
        helperText: ticketType.helperText ?? null,
      };
    }),
  };

  return (
    <Layout headerType={`float`}>
      <NextSeo
        title={`${event.title} | Someday`}
        description={description}
        openGraph={{
          title: `${event.title} | Someday`,
          description: description,
          images: [
            {
              url: event.imgUrl,
            },
          ],
        }}
      />
      <EventDetail {...eventDetailProps} />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { query } = context;
    const { id, promoCode } = query;

    if (typeof id !== `string`) {
      return {
        notFound: true,
      };
    }

    const { data } = await client.query<EventQuery, EventQueryVariables>({
      query: EventDocument,
      variables: { id, promoCode: (promoCode as string) || null },
    });

    if (!data.event) {
      return {
        notFound: true,
      };
    }

    const description = await remark()
      .use(strip)
      .process(data.event.description)
      .then(String)
      .then((it) => it.slice(0, 250));

    return {
      props: { event: data.event, description },
    };
  } catch (error) {
    captureException(error);

    return {
      redirect: {
        destination: `/500`,
        permanent: false,
      },
    };
  }
};
